/**
 * Quanto do orçamento cada fornecedor vencedor detém.
 *
 * Forma: barra empilhada horizontal — parte-do-todo com nomes longos. Teto de 6
 * segmentos (5 fornecedores + "Outros"): acima disso a leitura vira adivinhação
 * e a cauda vai para a tabela. Os hues saem da ordem fixa dos slots, nunca
 * gerados nem ciclados, e a cor segue o fornecedor, não a posição no ranking.
 *
 * Separação entre segmentos é um gap de 2px na cor da superfície, não um contorno:
 * contorno adiciona tinta que não é dado.
 */

import React from "react";
import { Cartao, SemDados, moeda, percentual, useDica } from "./viz";
import type { FatiaFornecedor, Insights } from "@utils/planilha-insights";

const MAX_SEGMENTOS = 5;
const ALTURA = 40;
const GAP = 2;

export const ConcentracaoFornecedores: React.FC<{ insights: Insights }> = ({
  insights,
}) => {
  const { mostrar, esconder, Dica } = useDica<FatiaFornecedor>();

  if (insights.concentracao.length === 0) {
    return (
      <Cartao titulo="Concentração por fornecedor">
        <SemDados>
          Nenhum item tem cotação vencedora ainda. O painel preenche assim que as
          propostas chegarem.
        </SemDados>
      </Cartao>
    );
  }

  const principais = insights.concentracao.slice(0, MAX_SEGMENTOS);
  const cauda = insights.concentracao.slice(MAX_SEGMENTOS);
  const somaCauda = cauda.reduce((s, f) => s + f.valor, 0);
  const fracaoCauda = cauda.reduce((s, f) => s + f.fracao, 0);

  const segmentos: Array<FatiaFornecedor & { cor: string; agrupado?: boolean }> = [
    ...principais.map((f, i) => ({ ...f, cor: `var(--viz-s${i + 1})` })),
    ...(cauda.length > 0
      ? [
          {
            nome: `Outros ${cauda.length} fornecedores`,
            valor: somaCauda,
            fracao: fracaoCauda,
            cor: "var(--viz-neutral)",
            agrupado: true,
          },
        ]
      : []),
  ];

  const larguraTotal = 720;
  const gaps = GAP * Math.max(0, segmentos.length - 1);
  const util = larguraTotal - gaps;

  let x = 0;
  const desenhados = segmentos.map((s) => {
    const largura = Math.max(2, s.fracao * util);
    const atual = x;
    x += largura + GAP;
    return { ...s, x: atual, largura };
  });

  const maior = insights.maiorFatia;
  const alertaConcentracao = maior && maior.fracao >= 0.4;

  return (
    <Cartao
      titulo="Concentração por fornecedor"
      descricao="Fatia do orçamento nas mãos de cada vencedor. Fatia grande é dependência."
    >
      <div className="relative overflow-x-auto">
        <svg
          width={larguraTotal}
          height={ALTURA + 10}
          role="img"
          aria-label="Participação de cada fornecedor vencedor no orçamento"
        >
          {desenhados.map((s) => {
            // Rótulo dentro do segmento só quando o texto cabe com folga.
            const cabeDentro = s.largura >= 62;
            return (
              <g
                key={s.nome}
                onMouseMove={(e) => mostrar(e, s)}
                onMouseLeave={esconder}
                tabIndex={0}
                onFocus={(e) => mostrar(e as any, s)}
                onBlur={esconder}
              >
                <rect x={s.x} y={0} width={s.largura} height={ALTURA} rx={3} fill={s.cor} />
                {cabeDentro && (
                  <text
                    x={s.x + 8}
                    y={ALTURA / 2 + 4}
                    className="text-[11px] font-semibold tabular-nums"
                    // Dentro de preenchimento colorido, a tinta é escolhida pela
                    // luminância do fundo — única exceção à regra de tinta de texto.
                    fill={s.agrupado ? "var(--viz-ink)" : "#ffffff"}
                  >
                    {percentual(s.fracao)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        <Dica>
          {(f) => (
            <>
              <p className="font-semibold text-base-content">{f.nome}</p>
              <dl className="mt-1 space-y-0.5 text-base-content/70">
                <div className="flex justify-between gap-4">
                  <dt>Valor</dt>
                  <dd className="tabular-nums">{moeda(f.valor)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Fatia</dt>
                  <dd className="tabular-nums">{percentual(f.fracao, 1)}</dd>
                </div>
              </dl>
            </>
          )}
        </Dica>
      </div>

      {/* Legenda sempre presente: a identidade não pode depender só da cor. */}
      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {desenhados.map((s) => (
          <li key={s.nome} className="flex items-center gap-2 text-sm">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ background: s.cor }}
            />
            <span className="min-w-0 flex-1 truncate text-base-content/80" title={s.nome}>
              {s.nome}
            </span>
            <span className="shrink-0 tabular-nums text-base-content/60">
              {moeda(s.valor, true)}
            </span>
          </li>
        ))}
      </ul>

      {alertaConcentracao && (
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-warning/10 px-3 py-2 text-sm">
          {/* Cor de status nunca carrega sentido sozinha: vem com ícone e rótulo. */}
          <span aria-hidden>⚠️</span>
          <span className="text-base-content/80">
            <strong>{maior!.nome}</strong> concentra {percentual(maior!.fracao)} do
            orçamento. Vale avaliar dependência e capacidade de entrega.
          </span>
        </p>
      )}
    </Cartao>
  );
};
